import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Upload, ChevronRight, FolderPlus } from 'lucide-react';
import { toast } from 'sonner';
import api, { getFileUrl } from '../../lib/api';
import RichTextEditor from '../../components/admin/RichTextEditor';

export default function CustomPageManager() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title:'',slug:'',content:'',parent_id:'',position:0,is_active:true });
  const [coverFile, setCoverFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetch = useCallback(() => { api.get('/custom-pages').then(r => setPages(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const topMenus = pages.filter(p => !p.parent_id || p.parent_id === 'None');
  const getChildren = (parentId) => pages.filter(p => p.parent_id === parentId).sort((a,b) => (parseInt(a.position)||0) - (parseInt(b.position)||0));

  const resetForm = () => { setForm({ title:'',slug:'',content:'',parent_id:'',position:0,is_active:true }); setCoverFile(null); setEditing(null); setShowForm(false); };

  const handleEdit = (p) => {
    setEditing(p);
    setForm({ title:p.title||'', slug:p.slug||'', content:p.content||'', parent_id:p.parent_id||'', position:parseInt(p.position)||0, is_active:p.is_active===true||p.is_active==='true'||p.is_active==='True' });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Sayfa adı gerekli'); return; }
    setIsSubmitting(true);
    try {
      let cover = editing?.cover_image || '';
      if (coverFile) { const fd = new FormData(); fd.append('file', coverFile); const r = await api.post('/uploads', fd); cover = r.data.path; }
      const payload = { ...form, cover_image: cover };
      if (editing) { await api.put(`/custom-pages/${editing.id}`, payload); toast.success('Güncellendi'); }
      else { await api.post('/custom-pages', payload); toast.success('Oluşturuldu'); }
      resetForm(); fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata oluştu'); }
    finally { setIsSubmitting(false); }
  };

  const handleDelete = async (id) => { if (!window.confirm('Bu sayfayı ve tüm alt sayfalarını silmek istediğinize emin misiniz?')) return; try { await api.delete(`/custom-pages/${id}`); toast.success('Silindi'); fetch(); } catch { toast.error('Hata'); } };

  const addSubPage = (parentId) => { resetForm(); setForm(f => ({ ...f, parent_id: parentId })); setShowForm(true); };

  return (
    <div data-testid="admin-custom-page-manager">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="font-heading text-2xl font-semibold">Özel Sayfalar</h1>
          <p className="text-sm text-muted-foreground mt-1">Standart menülerin dışında özel menü ve alt sayfalar oluşturun</p>
        </div>
        <button onClick={() => { resetForm(); setShowForm(true); }} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium"><Plus className="w-4 h-4" /> Yeni Ana Menü</button>
      </div>

      {showForm && (
        <div className="my-6 p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <h3 className="font-heading text-lg font-semibold mb-4">{editing ? 'Sayfa Düzenle' : form.parent_id ? 'Yeni Alt Sayfa' : 'Yeni Ana Menü'}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium mb-1.5">Sayfa Adı *</label><input value={form.title} onChange={e => setForm(f=>({...f,title:e.target.value}))} placeholder="Örn: Künye" className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]" /></div>
              <div><label className="block text-sm font-medium mb-1.5">Slug</label><input value={form.slug} onChange={e => setForm(f=>({...f,slug:e.target.value}))} placeholder="otomatik oluşturulur" className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm" /></div>
              <div><label className="block text-sm font-medium mb-1.5">Üst Menü</label>
                <select value={form.parent_id} onChange={e => setForm(f=>({...f,parent_id:e.target.value}))} className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm">
                  <option value="">Ana Menü (Üst Seviye)</option>
                  {topMenus.map(m => <option key={m.id} value={m.id}>{m.title}</option>)}
                </select>
              </div>
              <div><label className="block text-sm font-medium mb-1.5">Sıra</label><input type="number" value={form.position} onChange={e => setForm(f=>({...f,position:parseInt(e.target.value)||0}))} className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm" /></div>
            </div>
            <div><label className="block text-sm font-medium mb-1.5">Sayfa İçeriği</label><RichTextEditor value={form.content} onChange={(html) => setForm(f=>({...f,content:html}))} testId="custom-page-content" /></div>
            <div><label className="block text-sm font-medium mb-1.5">Kapak Görseli</label>
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-input bg-background text-sm hover:bg-secondary"><Upload className="w-4 h-4" /> Görsel Seç<input type="file" accept="image/*" className="hidden" onChange={e => setCoverFile(e.target.files[0])} /></label>
              <span className="text-xs text-muted-foreground ml-2">{coverFile?.name || ''}</span>
              {editing?.cover_image && <img src={getFileUrl(editing.cover_image)} alt="" className="mt-2 h-16 rounded-lg object-cover" />}
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={e => setForm(f=>({...f,is_active:e.target.checked}))} className="rounded" /> Aktif</label>
            <div className="flex gap-3">
              <button type="submit" disabled={isSubmitting} className="px-6 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium disabled:opacity-50">{isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}</button>
              <button type="button" onClick={resetForm} className="px-6 py-2.5 rounded-lg border border-input text-sm hover:bg-secondary">İptal</button>
            </div>
          </form>
        </div>
      )}

      {loading ? <div className="flex justify-center py-10"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>
      : topMenus.length === 0 ? <div className="text-center py-10 text-muted-foreground mt-4">Henüz özel sayfa eklenmemiş</div>
      : <div className="space-y-4 mt-6">{topMenus.map(menu => {
        const children = getChildren(menu.id);
        return (
          <div key={menu.id} className="rounded-2xl border border-border overflow-hidden">
            <div className="flex items-center gap-4 p-4 bg-card">
              <div className="w-8 h-8 rounded-lg bg-[hsl(var(--gold)/0.1)] flex items-center justify-center shrink-0">
                <span className="font-heading font-bold text-sm text-[hsl(var(--gold))]">{menu.title?.[0]}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold text-sm">{menu.title}</div>
                <div className="text-xs text-muted-foreground">/{menu.slug} \u2022 {children.length} alt sayfa</div>
              </div>
              <span className={`px-2 py-0.5 text-xs rounded-full ${menu.is_active==='true'||menu.is_active===true ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                {menu.is_active==='true'||menu.is_active===true ? 'Aktif' : 'Pasif'}
              </span>
              <div className="flex gap-1">
                <button onClick={() => addSubPage(menu.id)} className="p-2 rounded-lg hover:bg-secondary" title="Alt sayfa ekle"><FolderPlus className="w-4 h-4 text-[hsl(var(--gold))]" /></button>
                <button onClick={() => handleEdit(menu)} className="p-2 rounded-lg hover:bg-secondary"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(menu.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            {children.length > 0 && (
              <div className="border-t border-border bg-secondary/20">
                {children.map(child => (
                  <div key={child.id} className="flex items-center gap-4 px-4 py-3 border-b border-border/50 last:border-0">
                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground ml-4" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{child.title}</div>
                      <div className="text-xs text-muted-foreground">/{child.slug}</div>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => handleEdit(child)} className="p-1.5 rounded-lg hover:bg-secondary"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleDelete(child.id)} className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}</div>}
    </div>
  );
}
